import { useState, useEffect } from "react"
import axios from "axios"
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { handleUpdateUserData, handleUpdateUserProfile } from "../../../pages/UserProfile"

const GradesForm = ({ user, setUser, apiToken, userData, setUserData }) => {

  const [loading, setLoading] = useState(false)
  const classRankNumberMin = 1
  const classRankSizeMin = 1

  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  useEffect(() => {
    const updateScreenSize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const isEmpty = (val: any) => val === '' || val === null || val === undefined;

  const formSchema = Yup.object().shape({
    gpaUnweighted: Yup.number()
      .nullable()
      .min(0, 'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)')
      .max(4.0, 'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)').test(
        'max-2-decimals',
        'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00). GPA must not exceed two decimal points.',
        (value) => {
          if (value === null || value === undefined) return true;
          return /^\d+(\.\d{1,2})?$/.test(String(value));
        }
      ),
    gpaWeighted: Yup.number()
      .nullable()
      .min(0, 'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)')
      .max(5.0, 'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)').test(
        'max-2-decimals',
        'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00). GPA must not exceed two decimal points.',
        (value) => {
          if (value === null || value === undefined) return true;
          return /^\d+(\.\d{1,2})?$/.test(String(value));
        }
      ),
    classRankNumber: Yup.number()
      .typeError('Class Rank value must be a number')
      .nullable()
      .min(classRankNumberMin, 'Class Rank must be greater than 0')
      .test(
        'require-classRankNumber-if-classRankSize',
        'Class rank must also be entered.',
        function (value) {
          const { classRankSize } = this.parent;
          const hasSize = classRankSize !== null && classRankSize !== undefined;
          const hasNumber = value !== null && value !== undefined;
          return !(hasSize && !hasNumber);
        }
      )
      .test(
        'rank-not-greater-than-size',
        'Class rank must not be greater than class size.',
        function (value) {
          const { classRankSize } = this.parent;
          const hasSize = classRankSize !== null && classRankSize !== undefined;
          const hasNumber = value !== null && value !== undefined;
          if (hasSize && hasNumber) {
            return value <= classRankSize;
          }
          return true;
        }
      ),
    classRankSize: Yup.number()
      .typeError('Class Size value must be a number')
      .nullable()
      .min(classRankSizeMin, 'Class Size must be greater than 0')
      .test(
        'require-classRankSize-if-classRankNumber',
        'Class size must also be entered.',
        function (value) {
          const { classRankNumber } = this.parent;
          const hasNumber = classRankNumber !== null && classRankNumber !== undefined;
          const hasSize = value !== null && value !== undefined;
          return !(hasNumber && !hasSize);
        }
      ),
    classRankTier: Yup.number().nullable(),
  })

  let initialValues = {
    gpaWeighted: userData?.gpaWeighted,
    gpaUnweighted: userData?.gpaUnweighted, classRankNumber: userData?.classRankNumber, classRankSize: userData?.classRankSize, classRankTier: userData?.classRankTier
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: formSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      const cleanedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [key, value === "" ? null : value])
      );
      try {
        handleUpdateUserData(user, cleanedValues, setUserData, apiToken);
        handleUpdateUserProfile(setUser, apiToken)
        console.log('Submitted these values:', cleanedValues)
        setLoading(false)
      } catch (error) {
        console.error(error)
        alert('Error encountered')
        setStatus('The grades details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    }
  })

  // const gpaWeightedField = (
  //   <div className="dca-userProfile-form-field">
  //     <input
  //       placeholder='Weighted GPA'
  //       {...formik.getFieldProps('gpaWeighted')}
  //       className={clsx(
  //         'form-control form-control-solid',
  //         { 'is-invalid': formik.touched.gpaWeighted && formik.errors.gpaWeighted },
  //         {
  //           'is-valid': formik.touched.gpaWeighted && !formik.errors.gpaWeighted,
  //         }
  //       )}
  //       type='number'
  //       name='gpaWeighted'
  //       autoComplete='off'
  //     />
  //     {formik.touched.gpaWeighted && formik.errors.gpaWeighted && (
  //       <div className='fv-plugins-message-container'>
  //         <span className='dca-userprofile-form-alert' role='alert'>GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)</span>
  //       </div>
  //     )}
  //   </div>
  // )

  const gpaWeightedField = (
    <div className="dca-userProfile-form-field">
      <input
        placeholder="Weighted GPA"
        name="gpaWeighted"
        type="number"
        autoComplete="off"
        value={formik.values.gpaWeighted ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          const parsed = parseFloat(raw);
          if (!isNaN(parsed) || raw === '') {
            formik.setFieldValue('gpaWeighted', raw);
          }
        }}
        onBlur={(e) => {
          const raw = e.target.value;
          const parsed = parseFloat(raw);
          if (!isNaN(parsed)) {
            formik.setFieldValue('gpaWeighted', parsed.toFixed(2));
          }
          formik.handleBlur(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const raw = e.currentTarget.value;
            const parsed = parseFloat(raw);
            if (!isNaN(parsed)) {
              formik.setFieldValue('gpaWeighted', parsed.toFixed(2));
            }
            e.currentTarget.blur();
          }
        }}
        className={clsx(
          'form-control form-control-solid',
          {
            'is-invalid':
              formik.touched.gpaWeighted && formik.errors.gpaWeighted,
          },
          {
            'is-valid':
              formik.touched.gpaWeighted && !formik.errors.gpaWeighted,
          }
        )}
      />
      {formik.touched.gpaWeighted && formik.errors.gpaWeighted && (
        <div className="fv-plugins-message-container">
          <span className="dca-userprofile-form-alert" role="alert">
            GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)
          </span>
        </div>
      )}
    </div>
  );

  // const gpaUnweightedField = (
  //   <div className="dca-userProfile-form-field">
  //     <input
  //       placeholder='Unweighted GPA'
  //       {...formik.getFieldProps('gpaUnweighted')}
  //       className={clsx(
  //         'form-control form-control-solid',
  //         { 'is-invalid': formik.touched.gpaUnweighted && formik.errors.gpaUnweighted },
  //         {
  //           'is-valid': formik.touched.gpaUnweighted && !formik.errors.gpaUnweighted,
  //         }
  //       )}
  //       type='number'
  //       name='gpaUnweighted'
  //       autoComplete='off'
  //     />
  //     {formik.touched.gpaUnweighted && formik.errors.gpaUnweighted && (
  //       <div className='fv-plugins-message-container'>
  //         <span role='alert' className='dca-userprofile-form-alert'>GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)</span>
  //       </div>
  //     )}
  //   </div>
  // )

  const gpaUnweightedField = (
    <div className="dca-userProfile-form-field">
      <input
        placeholder="Unweighted GPA"
        name="gpaUnweighted"
        type="number"
        autoComplete="off"
        value={formik.values.gpaUnweighted ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          const parsed = parseFloat(raw);
          if (!isNaN(parsed) || raw === '') {
            formik.setFieldValue('gpaUnweighted', raw);
          }
        }}
        onBlur={(e) => {
          const raw = e.target.value;
          const parsed = parseFloat(raw);
          if (!isNaN(parsed)) {
            formik.setFieldValue('gpaUnweighted', parsed.toFixed(2));
          }
          formik.handleBlur(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const raw = e.currentTarget.value;
            const parsed = parseFloat(raw);
            if (!isNaN(parsed)) {
              formik.setFieldValue('gpaUnweighted', parsed.toFixed(2));
            }
            e.currentTarget.blur();
          }
        }}
        className={clsx(
          'form-control form-control-solid',
          {
            'is-invalid':
              formik.touched.gpaUnweighted && formik.errors.gpaUnweighted,
          },
          {
            'is-valid':
              formik.touched.gpaUnweighted && !formik.errors.gpaUnweighted,
          }
        )}
      />
      {formik.touched.gpaUnweighted && formik.errors.gpaUnweighted && (
        <div className="fv-plugins-message-container">
          <span role="alert" className="dca-userprofile-form-alert">
            GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)
          </span>
        </div>
      )}
    </div>
  );

  const classRankNumberField = (
    <div className="dca-userProfile-form-field" style={{ width: '100%' }}>
      <input
        placeholder="Class Rank"
        {...formik.getFieldProps('classRankNumber')}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          const raw = e.target.value;
          if (raw === '') {
            formik.setFieldValue('classRankNumber', null);
          } else {
            const parsed = parseInt(raw, 10);
            if (!isNaN(parsed)) {
              formik.setFieldValue('classRankNumber', parsed);
            }
          }

          if (!formik.touched.classRankSize) {
            formik.setFieldTouched('classRankSize', true, true);
          }

          formik.handleBlur(e);
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;

          if (raw === '') {
            formik.setFieldValue('classRankNumber', null);
          } else if (!isNaN(Number(raw))) {
            formik.setFieldValue('classRankNumber', Number(raw));
          }
        }}
        className={clsx(
          'form-control form-control-solid',
          { 'is-invalid': formik.touched.classRankNumber && formik.errors.classRankNumber },
          { 'is-valid': formik.touched.classRankNumber && !formik.errors.classRankNumber }
        )}
        type="string"
        name="classRankNumber"
        autoComplete="off"
      />
      {formik.touched.classRankNumber && typeof formik.errors.classRankNumber === 'string' && (
        <div className='fv-plugins-message-container'>
          <span role='alert'>{formik.errors.classRankNumber}</span>
        </div>
      )}
    </div>
  )

  const classSizeField = (
    <div className="dca-userProfile-form-field" style={{ width: '100%' }}>
      <input
        placeholder="Class Size"
        {...formik.getFieldProps('classRankSize')}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          const raw = e.target.value;

          if (raw === '') {
            formik.setFieldValue('classRankSize', null);
          } else {
            const parsed = parseInt(raw, 10);
            if (!isNaN(parsed)) {
              formik.setFieldValue('classRankSize', parsed);
            }
          }

          if (!formik.touched.classRankNumber) {
            formik.setFieldTouched('classRankNumber', true, true);
          }

          formik.handleBlur(e);
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;

          if (raw === '') {
            formik.setFieldValue('classRankSize', null);
          } else if (!isNaN(Number(raw))) {
            formik.setFieldValue('classRankSize', Number(raw));
          }
        }}
        className={clsx(
          'form-control form-control-solid',
          { 'is-invalid': formik.touched.classRankSize && formik.errors.classRankSize },
          { 'is-valid': formik.touched.classRankSize && !formik.errors.classRankSize }
        )}
        type="string"
        name="classRankSize"
        autoComplete="off"
      />
      {formik.touched.classRankSize && typeof formik.errors.classRankSize === 'string' && (
        <div className='fv-plugins-message-container'>
          <span role='alert'>{formik.errors.classRankSize}</span>
        </div>
      )}
    </div>
  )

  const inputStyle = { width: '100%', borderRadius: '5px', border: 'transparent', background: '#F9F9F9' }

  const classRankTierField = (
    <div className="dca-userProfile-form-field">
      <select
        className='form-select'
        data-control="select2"
        {...formik.getFieldProps('classRankTier')}
        name="classRankTier"
        style={inputStyle}
      >
        {userData?.classRankTier === null ? <option value={''} hidden>Select Class Rank Tier</option> : <option value={''}>Clear</option>}
        <option value={1}>Top 1%</option>
        <option value={2}>Top 5%</option>
        <option value={3}>Top 10%</option>
        <option value={4}>Top 25%</option>
        <option value={5}>Top 50%</option>
        <option value={6}>Bottom 50%</option>
      </select>
    </div>
  )

  return (

    <div>
      <form
        onSubmit={formik.handleSubmit}
        noValidate

        id="gradesForm">

        <div className='dca-userProfile-form-field-wrapper'>
          <div className='dca-userProfile-form-field-column'>
            <div className={`dca-userProfile-form-field ${isMobile ? 'gradesCustom' : ''}`}>GPA (weighted)</div>
            <div className={`dca-userProfile-form-field ${isMobile ? 'gradesCustom' : ''}`}>GPA (unweighted)</div>
            {isMobile ? <><div className={`dca-userProfile-form-field ${isMobile ? 'gradesCustom' : ''}`}>Class Rank</div><div className={`dca-userProfile-form-field ${isMobile ? 'gradesCustom' : ''}`}>Class Size</div></> : <div className='dca-userProfile-form-field'>Class Rank / Class Size</div>}
            <div className={`dca-userProfile-form-field ${isMobile ? 'gradesCustom' : ''}`}>Class Rank Tier</div>
          </div>

          <div className='d-flex flex-column' style={{ width: '100%' }}>
            {gpaWeightedField}
            {gpaUnweightedField}
            {isMobile ? <>
              {classRankNumberField}
              {classSizeField}
            </> : <div className='d-flex flex-row justify-content-evenly'>
              {classRankNumberField}
              {classSizeField}
            </div>}
            {classRankTierField}
          </div>
        </div>

        <div className='separator'></div>

        <div className='d-flex flex-row justify-content-end align-items-end'>
          <button
            className='dca-userProfile-discard-changes-button text-muted'
            onClick={(e) => {
              e.preventDefault();
              formik.setValues({
                gpaWeighted: userData.gpaWeighted ?? '', gpaUnweighted: userData.gpaUnweighted ?? '', classRankNumber: userData.classRankNumber ?? '', classRankSize: userData.classRankSize ?? '', classRankTier: userData.classRankTier ?? ''
              })
            }}>Discard</button>

          <button
            type='submit'
            id='gradesForm_submit'
            className='dca-userProfile-save-changes-button'
            disabled={formik.isSubmitting || !formik.isValid || !formik.values}
          >
            {!loading && <span className='indicator-label'>Save Changes</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form >
    </div >
  )

}

export { GradesForm }